import { useCallback, useEffect, useState } from 'react';
import { Link2, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import apiService from '../../services/api';
import { getOrCreateDeviceId } from '../../offline/deviceIdentity';
import { buildCommercantCaissePostesSettingsHref } from '../../utils/posCaisseSettingsNav';
import { useAdminSessionUser } from '../../hooks/useAdminSessionUser';

/**
 * Association guidée appareil ↔ poste sur l'écran d'ouverture caisse (sans enrôlement silencieux).
 */
export default function PosCaisseDeviceEnrollment({ commercantId, onEnrolled }) {
  const sessionUser = useAdminSessionUser();
  const isSuperAdmin = sessionUser?.role === 'SUPER_ADMIN';
  const roleInterne = sessionUser?.roleInterne;
  const canManage =
    isSuperAdmin || roleInterne === 'GERANT' || roleInterne === 'MANAGER';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [postes, setPostes] = useState([]);
  const [singlePosteMode, setSinglePosteMode] = useState(true);
  const localDeviceId = getOrCreateDeviceId();

  const load = useCallback(async () => {
    if (!commercantId) return;
    setLoading(true);
    setError('');
    try {
      const data = canManage
        ? await apiService.getCaissePostesAdmin(commercantId)
        : await apiService.getCaissePostes(commercantId);
      setPostes((data.postes || []).filter((p) => p.actif !== false));
      setSinglePosteMode(data.singlePosteMode !== false);
    } catch (err) {
      setError(err?.data?.error?.message || err?.message || 'Impossible de charger les postes de caisse');
    } finally {
      setLoading(false);
    }
  }, [commercantId, canManage]);

  useEffect(() => {
    load();
  }, [load]);

  const enroll = async (posteId) => {
    setSaving(true);
    setError('');
    try {
      await apiService.enrollCaissePosteDevice(commercantId, posteId, localDeviceId);
      await onEnrolled?.();
    } catch (err) {
      setError(err?.data?.error?.message || err?.message || 'Association impossible');
    } finally {
      setSaving(false);
    }
  };

  if (!canManage) {
    return (
      <p className="text-sm text-slate-600 mt-3">
        Demandez à un gérant ou manager d&apos;associer cet appareil dans{' '}
        <Link
          to={buildCommercantCaissePostesSettingsHref(commercantId)}
          className="font-semibold text-emerald-700 underline"
        >
          Paramètres → Caisses
        </Link>
        .
      </p>
    );
  }

  if (loading) {
    return (
      <p className="inline-flex items-center gap-2 text-sm text-slate-600 mt-3">
        <Loader2 className="h-4 w-4 animate-spin" />
        Chargement des postes…
      </p>
    );
  }

  const principal =
    postes.find((p) => p.code === 'PRINCIPALE') || (postes.length === 1 ? postes[0] : null);
  const activePostes = postes.filter((p) => p.actif !== false);

  return (
    <div
      data-testid="pos-caisse-device-enrollment"
      className="mt-4 rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm"
    >
      <p className="text-sm font-semibold text-slate-900">Associer cet appareil</p>
      <p className="text-xs text-slate-500 mt-1">
        Identifiant local :{' '}
        <code className="bg-slate-100 px-1 rounded text-[11px]">{localDeviceId}</code>
      </p>

      {error ? <p className="text-sm text-red-600 mt-2">{error}</p> : null}

      {activePostes.length === 0 ? (
        <p className="text-sm text-slate-600 mt-3">
          Aucun poste configuré.{' '}
          <Link
            to={buildCommercantCaissePostesSettingsHref(commercantId)}
            className="font-semibold text-emerald-700 underline"
          >
            Créer un poste dans Paramètres
          </Link>
          .
        </p>
      ) : singlePosteMode && principal ? (
        <button
          type="button"
          data-testid="pos-caisse-enroll-principal"
          disabled={saving}
          onClick={() => enroll(principal.id)}
          className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-50"
        >
          <Link2 className="h-4 w-4" />
          {saving ? 'Association…' : `Associer à ${principal.label || 'Caisse principale'}`}
        </button>
      ) : (
        <ul className="mt-3 space-y-2">
          {activePostes.map((poste) => (
            <li key={poste.id}>
              <button
                type="button"
                data-testid={`pos-caisse-enroll-${poste.code}`}
                disabled={saving}
                onClick={() => enroll(poste.id)}
                className="flex w-full items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm hover:border-emerald-300 hover:bg-emerald-50 disabled:opacity-50"
              >
                <span className="font-medium text-slate-800">{poste.label}</span>
                <span className="text-xs text-slate-500">{poste.code}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <Link
        to={buildCommercantCaissePostesSettingsHref(commercantId)}
        className="inline-block mt-3 text-xs font-semibold text-slate-600 underline hover:text-slate-900"
      >
        Gérer les postes et appareils →
      </Link>
    </div>
  );
}
